import { useEffect, useId, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import styles from './RemoveMemberDialog.module.scss'

import { isApiError } from '@/api'
import type { TeamResponse } from '@/api/generated'
import { moveMemberToTeam } from '@/api/members'
import ConfirmDialog from '@/components/ConfirmDialog'
import FormErrorBanner from '@/components/FormErrorBanner'
import Select from '@/components/Select'
import { enterpriseTeamsQueryKey } from '@/hooks/useEnterpriseTeams'
import { useToast } from '@/components/Toast/ToastContext'
import {
  MY_ACTIVE_COST_CENTER_QUERY_KEY,
  pickDefaultDestinationTeamId,
  TEAM_MEMBERS_TABLE_QUERY_KEY,
} from '@/pages/app/Team/Department/utils'

interface RemoveMemberDialogProps {
  isOpen: boolean
  /** The team the member is currently in. Used for copy + to exclude from the destination dropdown. */
  sourceTeam: TeamResponse | null
  /** All teams in the enterprise — used to populate the destination dropdown. */
  allTeams: TeamResponse[]
  /** The member being moved. */
  member: { userId: string; email: string | null } | null
  /** Enterprise name, used in the body copy. */
  enterpriseName: string
  enterpriseId: string
  onClose: () => void
}

const RemoveMemberDialog = ({
  isOpen,
  sourceTeam,
  allTeams,
  member,
  enterpriseName,
  enterpriseId,
  onClose,
}: RemoveMemberDialogProps) => {
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const selectId = useId()

  const [destinationId, setDestinationId] = useState<string>('')
  const [formError, setFormError] = useState<string | null>(null)

  const destinationOptions = useMemo(() => {
    if (!sourceTeam) return []
    return allTeams.filter((t) => t.id !== sourceTeam.id).map((t) => ({ value: t.id, label: t.name }))
  }, [allTeams, sourceTeam])

  useEffect(() => {
    if (!isOpen || !sourceTeam) return
    const def = pickDefaultDestinationTeamId(allTeams, sourceTeam)
    setDestinationId(def ?? '')
    setFormError(null)
  }, [isOpen, sourceTeam, allTeams])

  const mutation = useMutation({
    mutationFn: async () => {
      if (!member) throw new Error('No member selected')
      if (!destinationId) throw new Error('Pick a destination department')
      await moveMemberToTeam(destinationId, { user_id: member.userId })
    },
    onSuccess: () => {
      updateToast('Member moved', 'success')
      queryClient.invalidateQueries({ queryKey: enterpriseTeamsQueryKey(enterpriseId) })
      queryClient.invalidateQueries({ queryKey: [TEAM_MEMBERS_TABLE_QUERY_KEY] })
      // The moved member's team_id changes — refresh the CC-wide snapshot so
      // a subsequent Create-Department open sees the post-move roster.
      queryClient.invalidateQueries({ queryKey: [MY_ACTIVE_COST_CENTER_QUERY_KEY] })
      onClose()
    },
    onError: (err: unknown) => {
      setFormError(isApiError(err) ? (err.detail ?? 'Failed to move member.') : 'Failed to move member.')
    },
  })

  if (!sourceTeam || !member) return null

  const memberLabel = member.email ?? 'this member'

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={`Remove ${memberLabel} from ${sourceTeam.name}?`}
      minWidth={460}
      confirmLabel={mutation.isPending ? 'Saving…' : 'Save'}
      confirmType="dark"
      isConfirming={mutation.isPending}
      isConfirmDisabled={!destinationId}
      onConfirm={() => mutation.mutate()}
      onCancel={() => {
        if (mutation.isPending) return
        onClose()
      }}
      message={
        <div className={styles.body}>
          <p className={styles.helper}>
            Please select a new department for them to join. This action does not delete this person from your{' '}
            {enterpriseName} bill.
          </p>

          <div className={styles.field}>
            <label htmlFor={selectId} className={styles.label}>
              Move to
            </label>
            <Select
              id={selectId}
              value={destinationId}
              options={destinationOptions}
              onChange={setDestinationId}
              placeholder="Pick a department"
              isDisabled={mutation.isPending}
            />
          </div>

          {formError && <FormErrorBanner message={formError} />}
        </div>
      }
    />
  )
}

export default RemoveMemberDialog
